import React, { useState, useEffect } from 'react';
import Tooltip from './Tooltip';

// Aviso "este produto está em outras listas — replicar o preço?".
// itens: [{ codpro, descricao, valores: { preco_diretoria, preco_12x, comissao }, destinos: [linha de POST /outras-listas] }]
// modo 'um' (acabou de mexer num produto) ou 'lote' (Ações → Alinhar com outras listas).

const fmt = (v) => v != null ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const r2 = (v) => Math.round(v * 100) / 100;
const igual = (a, b) => (a == null && b == null) || (a != null && b != null && Math.abs(a - b) < 0.005);

// Mesma regra do backend (POST /replicar-precos): 12x só em lista com 12x; sem 12x na origem vai o padrão.
export const novo12xPara = (valores, dest) => {
  if (!dest.tem_preco_12x) return null;
  return valores.preco_12x != null ? valores.preco_12x : r2(valores.preco_diretoria * 1.2);
};

// A outra lista está diferente do que seria replicado?
export const destinoDiverge = (valores, dest) => {
  if (!igual(dest.preco_diretoria, valores.preco_diretoria)) return true;
  if (dest.tem_preco_12x && !igual(dest.preco_12x, novo12xPara(valores, dest))) return true;
  if (valores.comissao != null && !igual(dest.comissao, valores.comissao)) return true;
  return false;
};

const chave = (codpro, dest) => `${codpro}:${dest.produto_id}`;

const Valor = ({ rotulo, de, para }) => (
  <span className="me-3" style={{ whiteSpace: 'nowrap' }}>
    <span className="text-muted">{rotulo} </span>
    {igual(de, para)
      ? <span>{fmt(para)}</span>
      : <><span className="text-muted" style={{ textDecoration: de != null ? 'line-through' : 'none' }}>{de != null ? fmt(de) : 'vazio'}</span> → <b>{fmt(para)}</b></>}
  </span>
);

export default function ReplicarPrecosModal({ modo, itens, replicando, onFechar, onConfirmar }) {
  const [marcados, setMarcados] = useState(new Set());
  const [sempre, setSempre] = useState(false);

  // Abre com tudo marcado — a aprovação de cada lista fica como está, então não há o que poupar
  useEffect(() => {
    const s = new Set();
    (itens || []).forEach(it => it.destinos.forEach(d => s.add(chave(it.codpro, d))));
    setMarcados(s);
    setSempre(false);
  }, [itens]);

  if (!itens || !itens.length) return null;

  const total = itens.reduce((n, it) => n + it.destinos.length, 0);
  const alternar = (k) => setMarcados(prev => { const s = new Set(prev); s.has(k) ? s.delete(k) : s.add(k); return s; });
  const todos = (ligar) => {
    const s = new Set();
    if (ligar) itens.forEach(it => it.destinos.forEach(d => s.add(chave(it.codpro, d))));
    setMarcados(s);
  };
  const confirmar = () => {
    const sel = itens
      .map(it => ({ ...it, destinos: it.destinos.filter(d => marcados.has(chave(it.codpro, d))) }))
      .filter(it => it.destinos.length);
    onConfirmar(sel, sempre);
  };
  const algumErp = itens.some(it => it.destinos.some(d => d.no_erp && marcados.has(chave(it.codpro, d))));

  const linhaDestino = (it, d) => {
    const k = chave(it.codpro, d);
    return (
      <label key={k} className="d-flex align-items-start gap-2 py-2 px-2 rounded mb-1"
        style={{ cursor: 'pointer', background: marcados.has(k) ? '#F5EBF2' : '#fafafa', border: '1px solid #eee' }}>
        <input type="checkbox" className="form-check-input mt-1" checked={marcados.has(k)} onChange={() => alternar(k)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex flex-wrap align-items-center gap-1 mb-1">
            <b style={{ fontSize: '0.85rem' }}>{d.campanha_nome}</b>
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.68rem' }}>{d.status}</span>
            {d.aprovado
              ? <span className="badge" style={{ background: '#e9f6ee', color: '#1f7a3d', fontSize: '0.68rem' }}>aprovado · continua aprovado</span>
              : <span className="badge" style={{ background: '#f1f1f1', color: '#666', fontSize: '0.68rem' }}>pendente · continua pendente</span>}
            {d.tipo_precificacao === 'prazo' && (
              <Tooltip texto="Essa lista calcula o preço a partir do preço a prazo. O preço replicado entra como preço digitado à mão (fica destacado lá, com o botão de voltar ao calculado)." largura={260}>
                <span className="badge" style={{ background: '#fff8e1', color: '#a86400', fontSize: '0.68rem' }}><i className="bi bi-exclamation-triangle me-1"></i>a prazo · vira preço manual</span>
              </Tooltip>
            )}
            {d.no_erp && (
              <Tooltip texto="Essa lista já foi enviada ao ERP. O preço muda aqui no sistema; para valer no ERP, abra a lista e clique em Sincronizar com o ERP." largura={260}>
                <span className="badge" style={{ background: '#fdeeee', color: '#b23b3b', fontSize: '0.68rem' }}><i className="bi bi-arrow-repeat me-1"></i>ERP · sincronizar depois</span>
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: '0.8rem' }}>
            <Valor rotulo="Preço" de={d.preco_diretoria} para={it.valores.preco_diretoria} />
            {d.tem_preco_12x && <Valor rotulo="12x" de={d.preco_12x} para={novo12xPara(it.valores, d)} />}
            {it.valores.comissao != null && <Valor rotulo="Comissão" de={d.comissao} para={it.valores.comissao} />}
          </div>
        </div>
      </label>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card p-4" style={{ width: '92%', maxWidth: modo === 'lote' ? 860 : 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <div>
            <h5 className="fw-bold mb-1"><i className="bi bi-files me-2" style={{ color: 'var(--mostarda)' }}></i>
              {modo === 'lote' ? 'Alinhar com outras listas' : 'Produto também está em outras listas'}
            </h5>
            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
              {modo === 'lote'
                ? <>{itens.length} produto(s) desta lista estão com preço diferente em {total} lugar(es) nas outras listas abertas do mesmo período.</>
                : <><b>{itens[0].codpro}</b> — {itens[0].descricao}. Replicar o preço desta lista nas demais?</>}
            </div>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onFechar} disabled={replicando}><i className="bi bi-x-lg"></i></button>
        </div>

        {modo === 'lote' && (
          <div className="d-flex gap-2 mb-2" style={{ fontSize: '0.8rem' }}>
            <button className="btn btn-link btn-sm p-0" onClick={() => todos(true)}>Marcar todos</button>·
            <button className="btn btn-link btn-sm p-0" onClick={() => todos(false)}>Desmarcar todos</button>
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {modo === 'lote'
            ? itens.map(it => (
              <div key={it.codpro} className="mb-3">
                <div className="mb-1" style={{ fontSize: '0.82rem' }}>
                  <b>{it.codpro}</b> — {it.descricao}
                  <span className="text-muted ms-2">aqui: {fmt(it.valores.preco_diretoria)}{it.valores.preco_12x != null ? ` · 12x ${fmt(it.valores.preco_12x)}` : ''}{it.valores.comissao != null ? ` · com. ${fmt(it.valores.comissao)}` : ''}</span>
                </div>
                {it.destinos.map(d => linhaDestino(it, d))}
              </div>
            ))
            : itens[0].destinos.map(d => linhaDestino(itens[0], d))}
        </div>

        {algumErp && (
          <div className="mt-2" style={{ fontSize: '0.78rem', color: '#b23b3b' }}>
            <i className="bi bi-info-circle me-1"></i>Listas marcadas com <b>ERP</b> precisam de "Sincronizar com o ERP" depois para o preço valer lá.
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          {modo === 'um' ? (
            <label className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input mt-0" checked={sempre} onChange={e => setSempre(e.target.checked)} />
              Replicar sempre, sem perguntar, enquanto eu estiver nesta tela
            </label>
          ) : <span />}
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={onFechar} disabled={replicando}>Não replicar</button>
            <button className="btn-primary-mkt" onClick={confirmar} disabled={replicando || !marcados.size}>
              {replicando ? <><span className="spinner-border spinner-border-sm me-1"></span>Replicando...</> : <><i className="bi bi-check-lg me-1"></i>Replicar ({marcados.size})</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
